// Palworld egg item catalogue. Three sizes × nine egg types, plus the plain
// Chikipi Egg food item. Data is used by /eggs, the egg guide pack and search.

export type EggSize = "Common" | "Large" | "Huge";

export interface EggType {
  /** Element/biome family, e.g. "Damp". "Common" is the neutral family. */
  family: string;
  element: string;
  biome: string;
  color: string;
  accent: string;
  /** Pals that commonly hatch from this family, small → huge. */
  hatches: Record<EggSize, string[]>;
}

export const EGG_FAMILIES: EggType[] = [
  {
    family: "Common", element: "Neutral", biome: "Windswept Hills & Sea Breeze Archipelago",
    color: "#e9dfc7", accent: "#b8a887",
    hatches: {
      Common: ["Lamball", "Cattiva", "Chikipi", "Vixy", "Nitewing"],
      Large: ["Nitewing", "Galeclaw", "Mossanda", "Katress", "Beakon"],
      Huge: ["Paladius", "Necromus", "Lyleen", "Faleris", "Shadowbeak"],
    },
  },
  {
    family: "Damp", element: "Water", biome: "Coastal cliffs, lakes and the western beaches",
    color: "#7fd3ef", accent: "#2f7fa8",
    hatches: {
      Common: ["Fuack", "Pengullet", "Teafant", "Celaray", "Gobfin"],
      Large: ["Surfent", "Kelpsea Ignis", "Azurobe", "Elphidran Aqua", "Broncherry Aqua"],
      Huge: ["Jormuntide", "Azurobe", "Suzaku Aqua", "Relaxaurus Lux", "Jormuntide Ignis"],
    },
  },
  {
    family: "Dark", element: "Dark", biome: "Night spawns, Bamboo Groves and Deep Bamboo Thicket",
    color: "#a98bd8", accent: "#5a3f8f",
    hatches: {
      Common: ["Depresso", "Daedream", "Cawgnito", "Hoocrates", "Killamari"],
      Large: ["Katress", "Maraith", "Felbat", "Nox", "Grintale"],
      Huge: ["Necromus", "Shadowbeak", "Bellanoir", "Selyne", "Blazehowl Noct"],
    },
  },
  {
    family: "Dragon", element: "Dragon", biome: "Twilight Dunes, Mount Obsidian and Feybreak ridges",
    color: "#8f7bff", accent: "#4b3ac9",
    hatches: {
      Common: ["Chillet", "Hoocrates", "Sweepa", "Dinossom", "Quivern"],
      Large: ["Quivern", "Elphidran", "Relaxaurus", "Dinossom Lux", "Astegon"],
      Huge: ["Jetragon", "Orserk", "Astegon", "Jormuntide", "Eidrolon"],
    },
  },
  {
    family: "Electric", element: "Electric", biome: "Ravine Entrance, Sealed Realm of the Thunder Dragon",
    color: "#ffe066", accent: "#c9a300",
    hatches: {
      Common: ["Sparkit", "Jolthog", "Univolt", "Dazzi", "Rayhound"],
      Large: ["Univolt", "Grizzbolt", "Rayhound", "Beakon", "Relaxaurus Lux"],
      Huge: ["Orserk", "Grizzbolt", "Jetragon", "Beakon", "Dinossom Lux"],
    },
  },
  {
    family: "Frozen", element: "Ice", biome: "Astral Mountains and the Land of Absolute Zero",
    color: "#bfe9ff", accent: "#4f93c0",
    hatches: {
      Common: ["Pengullet", "Swee", "Reindrix", "Foxcicle", "Cryolinx"],
      Large: ["Foxcicle", "Cryolinx", "Sibelyx", "Mammorest Cryst", "Vanwyrm Cryst"],
      Huge: ["Frostallion", "Frostallion Noct", "Sibelyx", "Ice Kingpaca", "Cryolinx"],
    },
  },
  {
    family: "Rocky", element: "Ground", biome: "Desolate Church, Deep Sand Dunes and mining ridges",
    color: "#d8c39b", accent: "#8a6f45",
    hatches: {
      Common: ["Rushoar", "Fuddler", "Dumud", "Digtoise", "Tombat"],
      Large: ["Anubis", "Digtoise", "Dumud", "Broncherry", "Reptyro"],
      Huge: ["Anubis", "Blazamut", "Astegon", "Menasting", "Warsect"],
    },
  },
  {
    family: "Scorching", element: "Fire", biome: "Mount Obsidian and the volcanic south-east",
    color: "#ffb27a", accent: "#c2461a",
    hatches: {
      Common: ["Foxparks", "Rooby", "Arsox", "Flambelle", "Kelpsea Ignis"],
      Large: ["Ragnahawk", "Blazehowl", "Wixen", "Pyrin", "Jormuntide Ignis"],
      Huge: ["Suzaku", "Blazamut", "Faleris", "Jormuntide Ignis", "Pyrin Noct"],
    },
  },
  {
    family: "Verdant", element: "Grass", biome: "Bamboo Groves, forests and the Verdant Brook",
    color: "#a8e6a1", accent: "#3d8b45",
    hatches: {
      Common: ["Tanzee", "Gumoss", "Flopie", "Cinnamoth", "Bristla"],
      Large: ["Mossanda", "Verdash", "Petallia", "Elphidran", "Broncherry"],
      Huge: ["Lyleen", "Lyleen Noct", "Warsect", "Mammorest", "Mossanda Lux"],
    },
  },
];

export interface Egg {
  slug: string;
  name: string;
  size: EggSize;
  family: string;
  element: string;
  biome: string;
  color: string;
  accent: string;
  /** In-game hatch timer at default (1.0) server rate. */
  hatchTime: string;
  rarity: "Common" | "Uncommon" | "Rare";
  hatches: string[];
  desc: string;
  /** Food item, not an incubator egg. */
  food?: boolean;
}

const SIZE_META: Record<EggSize, { hatch: string; rarity: Egg["rarity"]; note: string }> = {
  Common: { hatch: "~30 minutes", rarity: "Common", note: "early-game Pals and low-tier breeding results" },
  Large: { hatch: "~1 hour 10 minutes", rarity: "Uncommon", note: "mid-game combat Pals and strong base workers" },
  Huge: { hatch: "~2 hours 30 minutes", rarity: "Rare", note: "end-game and Legendary Pals" },
};

function eggName(size: EggSize, family: string): string {
  if (family === "Common") return size === "Common" ? "Common Egg" : `Large Common Egg`.replace("Large", size);
  return size === "Common" ? `${family} Egg` : `${size} ${family} Egg`;
}

export const EGGS: Egg[] = [
  ...(["Common", "Large", "Huge"] as EggSize[]).flatMap((size) =>
    EGG_FAMILIES.map((f) => {
      const name = eggName(size, f.family);
      const meta = SIZE_META[size];
      return {
        slug: name.toLowerCase().replace(/\s+/g, "-"),
        name,
        size,
        family: f.family,
        element: f.element,
        biome: f.biome,
        color: f.color,
        accent: f.accent,
        hatchTime: meta.hatch,
        rarity: meta.rarity,
        hatches: f.hatches[size],
        desc: `${name} is a ${size.toLowerCase()}-size Palworld egg found in ${f.biome}. It hatches ${f.element}-leaning ${meta.note} such as ${f.hatches[size].slice(0, 3).join(", ")}.`,
      } satisfies Egg;
    }),
  ),
  {
    slug: "egg-food-item",
    name: "Egg",
    size: "Common",
    family: "Food",
    element: "Neutral",
    biome: "Chikipi on a Ranch",
    color: "#fff3d1",
    accent: "#c9a44c",
    hatchTime: "Not hatchable",
    rarity: "Common",
    hatches: [],
    food: true,
    desc: "The plain Egg is a food item produced by Chikipi assigned to a Ranch. It cannot be incubated — cook it, or use it in Cake for breeding.",
  },
];

export const EGG_BY_SLUG: Record<string, Egg> = Object.fromEntries(EGGS.map((e) => [e.slug, e]));
export const HATCHABLE_EGGS = EGGS.filter((e) => !e.food);
